/**
 * Implements a read-only report.
 */

import Radio from "backbone.radio";
import ReportModel from "../../models/report";
import columnSelector from "../../utils/datatable/column-selector";
import columnConfigurator from "../../utils/datatable/column-configurator";
import DTLinkBehavior from "../../behaviors/datatable/link";
import DTChildGridBehavior from "../../behaviors/datatable/child-grid";
import DataTable from "./core";

import "../../../../sass/datatables-overrides.scss";

const channel = Radio.channel("report");

const DEFAULTS = {
  tagName: "table",
  className: "table table-striped table-bordered table-hover",
  template: false,
};

class Report extends DataTable {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [
      { behaviorClass: DTLinkBehavior },
      { behaviorClass: DTChildGridBehavior },
    ];
  }

  initialize(options) {
    // localize options
    this.dt_config = options.dt_config || {};
    this.endpoint = options.endpoint;
    this.columns = options.columns;
    this.customColumns = options.customColumns;
    this.route = options.route;
    this.params = options.params;
    this.data = options.data;
    this.linkClassName = options.linkClassName || "drilldown";
    this.afterAjaxEvent = options.afterAjaxEvent || "report-updated";

    // reset sort order
    this.dt_config.order = [];

    // select columns
    this.selectedColumns = columnSelector(this.columns, this.customColumns);

    // configure selected columns
    this.dt_config.columns = columnConfigurator(
      this.selectedColumns,
      this.linkClassName,
      this.route
    );

    // was an ajax endpoint set?
    if (this.endpoint) {
      // enable server side requests
      this.dt_config.serverSide = true;

      // set ajax endpoint in DT options
      this.dt_config.ajax = {
        url: this.endpoint,
        data: this._onBeforeAjax.bind(this),
        dataSrc: this._onAfterAjax.bind(this),
      };
    } else {
      // process and set local data
      this.dt_config.data = this.processData(this.data, this.selectedColumns);
    }

    // since we overrode lifecycle method, we need to call super
    if (super.initialize) super.initialize();
  }

  onBeforeRender() {
    if (this.dt_config.select) {
      this.$el.addClass("selectable");
    }

    // since we overrode lifecycle method, we need to call super
    if (super.onBeforeRender) super.onBeforeRender();
  }

  _onBeforeAjax(data) {
    // augment with report params
    Object.assign(data, this.params);

    // remove 'search' attribute from payload
    delete data.search;

    // remove 'columns' attribute from payload whenever sorting is not used
    if (_.isEmpty(data.order)) {
      delete data.columns;
    } else {
      // remove unused attributes
      data.columns.map((column) => {
        delete column.data;
        delete column.search;
        delete column.orderable;
        delete column.searchable;
        delete column.tooltip;
        delete column.visible;

        return column;
      });
    }

    // add custom columns when available
    if (this.customColumns) {
      data.customColumns = this.customColumns.join();
    }
  }

  _onAfterAjax(json) {
    // announce model has been updated
    // also pass custom columns to be used by export
    channel.trigger(this.afterAjaxEvent, {
      model: new ReportModel(json),
      columns: this.customColumns,
    });

    // Selects custom columns, if any, and creates a data object
    // suitable for DataTables.
    return this.processData(json.data, this.selectedColumns);
  }

  /**
   * Parses the data object and extracts values of columns found
   * in the columns array.
   *
   * This is a hack that needs to be addressed by having the web service
   * return an object that is better optimized for consumption by the
   * DataTables API.
   *
   * @param {Array} rows
   * @param {Array} selectedColumns
   * @returns {Array}
   */
  processData(rows, selectedColumns) {
    // get keys from column list
    const keys = selectedColumns.map((col) => col.elementId);

    // iterate over each data row
    const dataset = rows.map((row) => {
      // create an object with each column in its proper sequence
      const orderedRow = {};
      keys.map((key) => {
        // use key to reorder columns
        orderedRow[key] = row[key];
      });

      // filter ordered row and return only those props that are in the list of keys
      const filtered = _.filter(orderedRow, (value, key) => keys.includes(key));

      // merge keys and values
      const merged = _.object(keys, filtered);

      return merged;
    });

    return dataset;
  }
}

export { Report as default };
